import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ShoppingBag } from 'lucide-react';
import * as S from './styles';

export type FomoPurchaseItem = {
  customerName: string;
  productName: string;
};

const FIRST_NAME_MAX_GRAPHEMES = 7;
const FIRST_NAME_TRUNCATE_SUFFIX = '..';

type GraphemeSegment = { segment: string };

function toGraphemeClusters(s: string): string[] {
  try {
    const IntlSeg = (Intl as unknown as { Segmenter?: new (loc: string, opt: { granularity: 'grapheme' }) => { segment: (str: string) => IterableIterator<GraphemeSegment> } }).Segmenter;
    if (typeof IntlSeg === 'function') {
      return Array.from(new IntlSeg('ar', { granularity: 'grapheme' }).segment(s), (x) => x.segment);
    }
  } catch {
    /* متصفحات قديمة */
  }
  return Array.from(s);
}

/** أول اسم فقط، بحدّ ٧ أحرف (مخططات) ثم ... إن زاد */
function customerFirstNameLabel(name: string): string {
  const s = name.trim();
  if (!s) return 'عميل';
  const first = s.split(/\s+/)[0] ?? '';
  if (!first) return 'عميل';
  const g = toGraphemeClusters(first);
  if (g.length <= FIRST_NAME_MAX_GRAPHEMES) return g.join('');
  return g.slice(0, FIRST_NAME_MAX_GRAPHEMES).join('') + FIRST_NAME_TRUNCATE_SUFFIX;
}

function apiBase(): string {
  return import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com').replace(/\/$/, '');
}

type PurchaseFomoProps = {
  /** إن وُجدت تُستخدم بدل جلب الـ API */
  items?: FomoPurchaseItem[];
  /** مدة دورة الظهور/الاختفاء لكل إشعار بالملّي ثانية (افتراضي ٣ ثوانٍ) */
  displayDurationMs?: number;
  /** الانتظار بعد اختفاء الإشعار قبل إظهار التالي (افتراضي ٣٠ ثانية) */
  gapBetweenMs?: number;
  /** الانتظار بعد فتح الصفحة قبل أول إشعار (افتراضي ٥ ثوانٍ؛ ‎٠‎ = بدون انتظار) */
  initialDelayMs?: number;
  /** إعادة جلب طلبات عشوائية جديدة من الخادم */
  refetchIntervalMs?: number;
  /** عدد السجلات المطلوبة من الـ API (١–٢٠، افتراضي ٥) */
  apiLimit?: number;
};

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setReduced(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return reduced;
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function mapPurchasesPayload(raw: unknown): FomoPurchaseItem[] {
  if (!raw || typeof raw !== 'object') return [];
  const data = (raw as { data?: { purchases?: unknown } }).data;
  const purchases = data?.purchases;
  if (!Array.isArray(purchases)) return [];
  const out: FomoPurchaseItem[] = [];
  for (const row of purchases) {
    if (!row || typeof row !== 'object') continue;
    const customer_name = (row as { customer_name?: string }).customer_name;
    const product_name = (row as { product_name?: string }).product_name;
    const customerName = typeof customer_name === 'string' && customer_name.trim() ? customer_name.trim() : 'عميل';
    const productName = typeof product_name === 'string' ? product_name.trim() : '';
    if (productName) out.push({ customerName, productName });
  }
  return out;
}

/**
 * إشعار FOMO في زاوية الشاشة: عميل + منتج (تدوير تلقائي).
 * افتراضياً: انتظار ‎٥ ثوانٍ‎ بعد فتح الصفحة ثم أول إشعار؛ دورة ظهور/اختفاء ‎٣ ثوانٍ‎ ثم فجوة ‎٣٠ ثانية‎ قبل التالي.
 * بدون `items` يُجلب من ‎`/api/fomo/recent-purchases`‎ ويُحدَّث دورياً.
 */
export function PurchaseFomo({
  items,
  displayDurationMs = 3000,
  gapBetweenMs = 30_000,
  initialDelayMs = 5000,
  refetchIntervalMs = 90_000,
  apiLimit = 5,
}: PurchaseFomoProps) {
  const reducedMotion = usePrefersReducedMotion();
  const fomoUnlockedRef = useRef(initialDelayMs <= 0);
  const [fomoUnlocked, setFomoUnlocked] = useState(() => initialDelayMs <= 0);
  const pageOpenTimeRef = useRef(Date.now());
  const controlled = items !== undefined;
  const [remoteItems, setRemoteItems] = useState<FomoPurchaseItem[] | null>(() => (controlled ? [] : null));

  useEffect(() => {
    if (controlled) return undefined;

    let cancelled = false;
    const limit = Math.min(20, Math.max(1, apiLimit));

    const load = async () => {
      try {
        const res = await fetch(`${apiBase()}/api/fomo/recent-purchases?limit=${limit}`, {
          credentials: 'omit',
        });
        const json: unknown = await res.json().catch(() => null);
        const mapped = mapPurchasesPayload(json);
        if (!cancelled) setRemoteItems(mapped);
      } catch {
        if (!cancelled) setRemoteItems([]);
      }
    };

    void load();
    const refetchId = window.setInterval(load, refetchIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(refetchId);
    };
  }, [controlled, refetchIntervalMs, apiLimit]);

  const source = useMemo(() => {
    if (controlled) {
      return items!.length ? items! : [];
    }
    if (remoteItems === null) return null;
    return remoteItems;
  }, [controlled, items, remoteItems]);

  const [order, setOrder] = useState<FomoPurchaseItem[]>([]);
  const [index, setIndex] = useState(0);
  const [tick, setTick] = useState(0);
  const [showCard, setShowCard] = useState(() => initialDelayMs <= 0);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimer(), [clearTimer]);

  const advanceRef = useRef<(() => void) | null>(null);
  const advance = useCallback(() => {
    setIndex((i) => (i + 1 >= order.length ? 0 : i + 1));
    setTick((t) => t + 1);
  }, [order.length]);
  advanceRef.current = advance;

  useLayoutEffect(() => {
    if (source === null) return;
    clearTimer();
    if (!source.length) {
      setOrder([]);
      return;
    }
    setOrder(shuffle(source));
    setIndex(0);
    setTick((t) => t + 1);
    setShowCard(fomoUnlockedRef.current);
  }, [source, clearTimer]);

  /** أول ظهور بعد ‎initialDelayMs‎ من فتح الصفحة (إن تأخر الـ API يُختصر الانتظار المتبقي) */
  useEffect(() => {
    if (order.length < 1 || initialDelayMs <= 0) return undefined;
    if (fomoUnlockedRef.current) return undefined;
    const elapsed = Date.now() - pageOpenTimeRef.current;
    const wait = Math.max(0, initialDelayMs - elapsed);
    const id = window.setTimeout(() => {
      fomoUnlockedRef.current = true;
      setFomoUnlocked(true);
      setShowCard(true);
    }, wait);
    return () => window.clearTimeout(id);
  }, [order.length, initialDelayMs]);

  /**
   * الآلية الرئيسية: setTimeout كمصدر وحيد للتوقيت.
   * Safari على الجوال لا يُطلق animationend بشكل موثوق — لا نعتمد عليه أبداً.
   * الـ CSS animation بصرية فقط، والإخفاء/الظهور يتحكم فيهما هذا الـ effect.
   */
  useEffect(() => {
    if (order.length < 1 || !fomoUnlocked) return undefined;
    let cancelled = false;

    function hide() {
      if (cancelled) return;
      setShowCard(false);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (cancelled) return;
        advanceRef.current?.();
        setShowCard(true);
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          if (!cancelled) hide();
        }, displayDurationMs);
      }, gapBetweenMs);
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (!cancelled) hide();
    }, displayDurationMs);

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [order.length, fomoUnlocked, displayDurationMs, gapBetweenMs, clearTimer]);

  if (source === null) return null;
  if (!source.length || !order.length) return null;

  const current = order[Math.min(index, order.length - 1)]!;
  const displayName = customerFirstNameLabel(current.customerName);

  const runAnimation = !reducedMotion && order.length >= 1 && showCard;

  return (
    <S.Wrapper aria-live="polite" aria-atomic="true">
      {showCard ? (
        <S.Card
          key={tick}
          $runAnimation={runAnimation}
          $durationSec={displayDurationMs / 1000}
        >
          <S.IconWrap aria-hidden>
            <ShoppingBag size={18} strokeWidth={2} />
          </S.IconWrap>
          <S.TextBlock>
            <S.Label>شراءٌ للتو</S.Label>
            <S.NameProductLine
              title={`${displayName} — ${current.productName}`}
            >
              {displayName}
              {' — '}
              <S.ProductName>{current.productName}</S.ProductName>
            </S.NameProductLine>
          </S.TextBlock>
        </S.Card>
      ) : null}
    </S.Wrapper>
  );
}

export default PurchaseFomo;
